// src/services/api.ts
import axios from 'axios';
import { getServerUrl } from '../../config/server.config';

const getApiUrl = () => getServerUrl();

const api = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token and dynamic baseURL to requests
api.interceptors.request.use((config) => {
  // Set baseURL dynamically on each request
  config.baseURL = getApiUrl();

  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth endpoints
export const authAPI = {
  register: async (data: {
    email: string;
    password: string;
    referralCode?: string;
  }) => {
    const response = await api.post('/auth/register', data);
    return response.data;
  },

  login: async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password });
    
    // Handle both response formats
    const token = response.data.token || response.data.data?.token;
    const user = response.data.user || response.data.data?.user;
    
    if (token) {
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
    }
    
    return response.data;
  },

  verifyEmail: async (email: string, code: string) => {
    const response = await api.post('/auth/verify-email', { email, code });
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  },

  getCurrentUser: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },
};

// Onboarding chat endpoints - Updated for Subutai
export const onboardingAPI = {
  startChat: async (referralCode?: string, isUpgrade?: boolean) => {
    const endpoint = referralCode
      ? `/ai/subutai/start/${referralCode}`
      : '/ai/subutai/start';
    const response = await api.post(endpoint, {
      isUpgrade: isUpgrade || false
    });
    return response.data;
  },

  sendMessage: async (sessionId: string, message: string) => {
    const response = await api.post('/ai/subutai/message', {
      sessionId,
      message,
    });
    return response.data;
  },

  uploadDocument: async (
    sessionId: string,
    documentType: string,
    file: File
  ) => {
    const formData = new FormData();
    formData.append('sessionId', sessionId);
    formData.append('documentType', documentType);
    formData.append('document', file);

    const response = await api.post('/ai/subutai/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  getStatus: async () => {
    const response = await api.get('/ai/subutai/status');
    return response.data;
  },
};

export default api;